import { Autocomplete, Chip, TextField } from "@mui/material"
import { ColDef, GridApi, ICellRendererParams } from "ag-grid-community"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import TextFiledCellRender from "../contacts/textFieldCellRender"
import { emptyLabels } from "../shared/constants"
import GridComponent from "../shared/grid"
import { ImportEditableRow, ImportFieldKey } from "./import-users-shared.component"
import { SeatUsage } from "../../models/organization.model"
import { Role, RoleModel } from "../../models/roles.model"
import { PhoneNumbergroup, PhoneNumberModel } from "../../models/phone-number.model"
import { UtilityService } from "../../services/UtilityService"

interface IProps {
    Seats: SeatUsage[]
    Roles: RoleModel[]
    PhoneNumbers: PhoneNumberModel[]
    PhoneNumberGroups: PhoneNumbergroup[]
    selectedOrgId: string
    Rows: ImportEditableRow[]
    ErrorRows: ImportEditableRow[]
    OnRowsChange: (rows: ImportEditableRow[]) => void
    GetRowErrors: (rows: ImportEditableRow[]) => ImportEditableRow[]
}

const getRoleId = (roles: RoleModel[], roleName: string) =>
    roles.find((role) => role.Name?.toLowerCase() === roleName?.toLowerCase())?.ID

const splitMultiValue = (value?: string) =>
    (value || "").split(";").map((item) => item.trim()).filter(Boolean)

const normalizePhone = (value: string) =>
    UtilityService.unmaskPhoneNumberWithPlus(value?.trim() || "")

const isSamePhone = (left: string, right: string) =>
    normalizePhone(left) === normalizePhone(right)

const isSameGroupName = (left: string, right: string) =>
    left.trim().toLowerCase() === right.trim().toLowerCase()

const IMPORT_LISTBOX_PROPS = {
    style: { maxHeight: 360, paddingTop: 8, paddingBottom: 8 }
}

const dropdownComponentsProps = (minWidth: number) => ({
    popper: { sx: { minWidth, zIndex: 2000 } },
    paper: {
        sx: {
            "& .MuiAutocomplete-option": {
                minHeight: 52,
                py: 1.5
            }
        }
    }
})

const SelectCellRender = ({
    value,
    options,
    onChange,
    menuMinWidth = 260,
    placeholder = "",
    error = "",
    getError,
    getOptionSecondaryLabel

}: {
    value: string
    options: string[]
    onChange: (nextValue: string) => void
    menuMinWidth?: number
    placeholder?: string
    error?: string
    getError?: (nextValue: string) => string
    getOptionSecondaryLabel?: (option: string) => string
}) => {
    const [inputValue, setInputValue] = useState(value)
    const [localError, setLocalError] = useState(error)

    useEffect(() => {
        setInputValue(value)
    }, [value])

    useEffect(() => {
        setLocalError(error)
    }, [error])

    const commit = (nextValue: string) => {
        setInputValue(nextValue)
        setLocalError(getError ? getError(nextValue) : error)
        onChange(nextValue)
    }

    return (
        <div className="w-p-100 h-p-100 d-flex flex-direction">
            <div className="d-flex align-center justify-center h-p-100 w-p-100">
                <Autocomplete
                    fullWidth
                    sx={{ width: "100%", minWidth: "100%", "& .MuiOutlinedInput-root": { width: "100%" } }}
                    size="small"
                    freeSolo
                    options={options}
                    value={value}
                    inputValue={inputValue}
                    onChange={(_, nextValue) => commit((nextValue as string) || "")}
                    onInputChange={(_, nextValue, reason) => {
                        if (reason === "input" || reason === "clear") {
                            commit(nextValue)
                        } else {
                            setInputValue(nextValue)
                        }
                    }}
                    ListboxProps={IMPORT_LISTBOX_PROPS}
                    componentsProps={dropdownComponentsProps(menuMinWidth)}
                    classes={{ paper: "bg-color-menu-bg box-shadow-none dialogue-shadow" }}
                    renderOption={(optionProps, option) => (
                        <li {...optionProps} className={`${optionProps.className || ""} c-pointer h2 px-3 py-2 d-flex justify-between w-p-90`}>
                            <span>{option}</span>
                            {!!getOptionSecondaryLabel && <span>{getOptionSecondaryLabel(option)}</span>}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            placeholder={placeholder}
                            InputProps={{
                                ...params.InputProps,
                                classes: { input: "h6-regular" }
                            }}
                        />
                    )}
                />
            </div>
            <div className="w-p-100 contactinfo-email h6-regular color-primary-danger t-align-right">
                {localError ? localError : "\u00A0"}
            </div>
        </div>
    )
}

const SecondaryPhoneCellRender = ({
    value,
    onChange,
    error = "",
    options = [],
    getError,
    name,
    placeholder,
    maxItems,
    isValidOption
}: {
    value: string
    onChange: (nextValue: string) => void
    error?: string
    options: string[]
    getError?: (nextValue: string) => string
    name: string
    placeholder?: string
    maxItems?: number
    isValidOption?: (option: string) => boolean
}) => {
    const selected = splitMultiValue(value)
    const [localError, setLocalError] = useState(error)

    useEffect(() => {
        setLocalError(error)
    }, [error])

    const commit = (nextValue: string) => {
        setLocalError(getError ? getError(nextValue) : error)
        onChange(nextValue)
    }

    const optionIsValid = (option: string) => {
        if (isValidOption) return isValidOption(option)
        return options.some((item) => name === "SecondaryPhoneNumbers" ? isSamePhone(item, option) : isSameGroupName(item, option))
    }

    return (
        <div className="w-p-100 h-p-100 d-flex flex-direction">
            <div className="d-flex align-center justify-center h-p-100 w-p-100">
                <Autocomplete
                    multiple
                    freeSolo
                    fullWidth
                    sx={{ width: "100%" }}
                    size="small"
                    disableCloseOnSelect
                    options={options}
                    value={selected}
                    onChange={(_, nextValue) => {
                        const next = maxItems ? nextValue.slice(0, maxItems) : nextValue
                        commit(next.join(";"))
                    }}
                    ListboxProps={IMPORT_LISTBOX_PROPS}
                    componentsProps={dropdownComponentsProps(380)}
                    classes={{ input: "h6-regular color-text-field-font", paper: "bg-color-menu-bg box-shadow-none dialogue-shadow" }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            placeholder={placeholder || (name === "PhoneNumberGroups" ? "Select Line Groups" : "Select Switchable Numbers")}
                            InputProps={{
                                ...params.InputProps,
                                classes: { input: "h6-regular" }
                            }}
                        />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                            const valid = optionIsValid(option)
                            return (
                                <Chip
                                    {...getTagProps({ index })}
                                    label={option}
                                    sx={{
                                        backgroundColor: valid ? "#C4CBD2" : "#FF142B",
                                        color: valid ? "#3D4A56" : "#fff",
                                        "& .MuiChip-deleteIcon": {
                                            color: valid ? "#3D4A56" : "#fff"
                                        }
                                    }}
                                />
                            )
                        })
                    }
                />
            </div>
            <div className="w-p-100 contactinfo-email h6-regular color-primary-danger t-align-right">
                {localError ? localError : "\u00A0"}
            </div>
        </div>
    )
}

const ImportUsersGrid = (props: IProps) => {
    const [gridApi, setGridApi] = useState<GridApi>()
    const rowsRef = useRef(props.Rows)
    const propsRef = useRef(props)

    const errorRowsRef = useRef(props.ErrorRows)

    useEffect(() => {
        rowsRef.current = props.Rows
    }, [props.Rows])

    useEffect(() => {
        errorRowsRef.current = props.ErrorRows
    }, [props.ErrorRows])

    useEffect(() => {
        propsRef.current = props
    })

    const errorAfterEdit = useCallback((rowId: string, field: ImportFieldKey, nextValue: string) => {
        const nextRows = rowsRef.current.map((row) => {
            if (row.RowId !== rowId) return row
            const nextRow = { ...row, [field]: nextValue }
            const roleId = getRoleId(propsRef.current.Roles, nextRow.Role)
            if (field === "PhoneNumber") {
                nextRow.PrimaryPhoneNumber = nextValue
            }
            if (roleId !== Role.USER) {
                nextRow.SecondaryPhoneNumbers = ""
            }
            if (roleId !== Role.MANAGER) {
                nextRow.PhoneNumberGroups = ""
            }
            return nextRow
        })
        return propsRef.current.GetRowErrors(nextRows).find((row) => row.RowId === rowId)?.[field] ?? ""
    }, [])

    const applyField = useCallback((rowId: string, field: ImportFieldKey, nextValue: string) => {
        const nextRows = rowsRef.current.map((row) => {
            if (row.RowId !== rowId) return row
            const nextRow = { ...row, [field]: nextValue }
            const roleId = getRoleId(propsRef.current.Roles, nextRow.Role)
            if (field === "PhoneNumber") {
                nextRow.PrimaryPhoneNumber = nextValue
            }
            if (roleId !== Role.USER) {
                nextRow.SecondaryPhoneNumbers = ""
            }
            if (roleId !== Role.MANAGER) {
                nextRow.PhoneNumberGroups = ""
            }
            return nextRow
        })
        rowsRef.current = nextRows
        propsRef.current.OnRowsChange(nextRows)

        requestAnimationFrame(() => {
            if (!gridApi) return
            if (field === "Role") {
                gridApi.refreshCells({ columns: ["PhoneNumberGroups", "PrimaryPhoneNumber", "SecondaryPhoneNumbers"], force: true })
                gridApi.resetRowHeights()
            } else if (field === "PhoneNumber") {
                gridApi.refreshCells({ columns: ["SecondaryPhoneNumbers"], force: true })
            } else if (field === "PhoneNumberGroups") {
                gridApi.refreshCells({ columns: ["PrimaryPhoneNumber", "SecondaryPhoneNumbers"], force: true })
            }
        })
    }, [gridApi])

    const errorFor = (rowId: string, field: ImportFieldKey) =>
        errorRowsRef.current.find((row) => row.RowId === rowId)?.[field] ?? ""

    const renderTextField = useCallback((params: ICellRendererParams, field: ImportFieldKey) => {
        const row = params.data as ImportEditableRow
        return (
            <TextFiledCellRender
                Value={(params.value || "").toString()}
                Error={errorFor(row.RowId, field)}
                OnCommit={(nextValue) => applyField(row.RowId, field, nextValue)}
                OnValidateWhileTyping={(nextValue) => errorAfterEdit(row.RowId, field, nextValue)}
            />
        )
    }, [applyField, errorAfterEdit])

    const columnDefs: ColDef[] = useMemo(() => [
        {
            field: "FirstName",
            headerName: "First Name",
            minWidth: 180,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => renderTextField(params, "FirstName"),
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "LastName",
            headerName: "Last Name",
            minWidth: 180,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => renderTextField(params, "LastName"),
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "Email",
            headerName: "Email",
            minWidth: 220,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => renderTextField(params, "Email"),
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "Role",
            headerName: "Role",
            minWidth: 200,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => {
                const row = params.data as ImportEditableRow
                return (
                    <SelectCellRender
                        value={row.Role}
                        error={errorFor(row.RowId, "Role")}
                        getError={(nextValue) => errorAfterEdit(row.RowId, "Role", nextValue)}
                        options={props.Roles.filter(role => role.ID !== Role.SYSTEM_ADMIN).map(role => role.Name)}
                        onChange={(nextValue) => applyField(row.RowId, "Role", nextValue)}
                    />
                )
            },
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "SeatType",
            headerName: "Seat Type",
            minWidth: 240,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => {
                const row = params.data as ImportEditableRow
                return (
                    <SelectCellRender
                        value={row.SeatType}
                        error={errorFor(row.RowId, "SeatType")}
                        getError={(nextValue) => errorAfterEdit(row.RowId, "SeatType", nextValue)}
                        options={props.Seats.map(seat => seat.SeatType)}
                        getOptionSecondaryLabel={(option) => {
                            const seat = props.Seats.find((item) => item.SeatType === option)
                            return seat ? `${seat.Users.Total - seat.Users.Used} available` : ""
                        }}
                        menuMinWidth={280}
                        placeholder="Seat Type"
                        onChange={(nextValue) => applyField(row.RowId, "SeatType", nextValue)}
                    />
                )
            },
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "PhoneNumberGroups",
            headerName: "Line Groups",
            minWidth: 240,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => {
                const latest = rowsRef.current.find((item) => item.RowId === (params.data as ImportEditableRow).RowId) || params.data as ImportEditableRow
                const roleId = getRoleId(props.Roles, latest.Role)
                if (roleId !== Role.MANAGER) {
                    return (
                        <div className="w-p-100 h-p-100 d-flex align-center">
                            <div className="h6-regular color-text-field-label">{"\u00A0"}</div>
                        </div>
                    )
                }

                const validGroupNames = props.PhoneNumberGroups.map((group) => group.GroupName)
                return (
                    <SecondaryPhoneCellRender
                        value={latest.PhoneNumberGroups}
                        name="PhoneNumberGroups"
                        placeholder="Select Line Groups"
                        options={validGroupNames}
                        isValidOption={(option) => validGroupNames.some((name) => isSameGroupName(name, option))}
                        error={errorFor(latest.RowId, "PhoneNumberGroups")}
                        getError={(nextValue) => errorAfterEdit(latest.RowId, "PhoneNumberGroups", nextValue)}
                        onChange={(nextValue) => applyField(latest.RowId, "PhoneNumberGroups", nextValue)}
                    />
                )
            },
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "PrimaryPhoneNumber",
            headerName: "Primary Phone Number",
            minWidth: 260,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => {
                const latest = rowsRef.current.find((item) => item.RowId === (params.data as ImportEditableRow).RowId) || params.data as ImportEditableRow
                const roleId = getRoleId(props.Roles, latest.Role)
                const selectedGroupNames = splitMultiValue(latest.PhoneNumberGroups)
                const groupsForPrimary = roleId === Role.MANAGER && selectedGroupNames.length
                    ? props.PhoneNumberGroups.filter((group) => selectedGroupNames.some((name) => isSameGroupName(group.GroupName, name)))
                    : props.PhoneNumberGroups
                const groupPhoneNumberIds: string[] = groupsForPrimary.flatMap((x: any) => (x.PhoneNumbers || []).map((x: any) => x.phoneNumberId)) || []
                const filteredPhoneNumbers: PhoneNumberModel[] = props.PhoneNumbers?.filter(pn => groupPhoneNumberIds.includes(pn.ID));
                return (
                    <SelectCellRender
                        value={latest.PrimaryPhoneNumber || latest.PhoneNumber || ""}
                        error={errorFor(latest.RowId, "PhoneNumber")}
                        getError={(nextValue) => errorAfterEdit(latest.RowId, "PhoneNumber", nextValue)}
                        options={roleId === Role.MANAGER ? filteredPhoneNumbers.map(x => x.PhoneNumber) : props.PhoneNumbers.map(x => x.PhoneNumber)}
                        menuMinWidth={380}
                        placeholder="Select Phone Number"
                        onChange={(nextValue) => applyField(latest.RowId, "PhoneNumber", nextValue)}
                    />
                )
            },
            cellStyle: { display: "flex", alignItems: "center" }
        },
        {
            field: "SecondaryPhoneNumbers",
            headerName: "Secondary Phone Number",
            minWidth: 280,
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => {
                const latest = rowsRef.current.find((item) => item.RowId === (params.data as ImportEditableRow).RowId) || params.data as ImportEditableRow
                const roleId = getRoleId(props.Roles, latest.Role)
                if (roleId !== Role.USER) {
                    return (
                        <div className="w-p-100 h-p-100 d-flex align-center">
                            <div className="h6-regular color-text-field-label">{"\u00A0"}</div>
                        </div>
                    )
                }

                const primaryUnmasked = normalizePhone(latest.PrimaryPhoneNumber || latest.PhoneNumber || "")
                const allowedSecondary = props.PhoneNumbers.filter((phone) => {
                    const phoneNumber = normalizePhone(phone.PhoneNumber) || phone.PhoneNumber
                    const sameOrg = !phone.OrganizationID || !props.selectedOrgId || phone.OrganizationID === props.selectedOrgId
                    return sameOrg &&
                        !phone.IsDeleted &&
                        phoneNumber !== primaryUnmasked
                })
                const allowedSecondaryNumbers = allowedSecondary.map((phone) => phone.PhoneNumber)

                return (
                    <SecondaryPhoneCellRender
                        name="SecondaryPhoneNumbers"
                        value={latest.SecondaryPhoneNumbers}
                        options={allowedSecondaryNumbers}
                        maxItems={3}
                        placeholder="Select Switchable Numbers"
                        isValidOption={(option) => allowedSecondaryNumbers.some((phone) => isSamePhone(phone, option))}
                        error={errorFor(latest.RowId, "SecondaryPhoneNumbers")}
                        getError={(nextValue) => errorAfterEdit(latest.RowId, "SecondaryPhoneNumbers", nextValue)}
                        onChange={(nextValue) => applyField(latest.RowId, "SecondaryPhoneNumbers", nextValue)}
                    />
                )
            },
            cellStyle: { display: "flex", alignItems: "center" }
        }
    ], [applyField, renderTextField, errorAfterEdit, props.Roles, props.Seats, props.PhoneNumbers, props.PhoneNumberGroups, props.selectedOrgId])

    return (
        <div className="mb-1 h-45 import-users-grid">
            <GridComponent
                ColumnDefs={columnDefs}
                RowData={props.Rows}
                OnGridReady={(params) => setGridApi(params.api)}
                IsPaginationEnabled={true}
                EmptyLabel={emptyLabels.USERS}
                LockVisible={false}
                isHorizontalScroll={true}
                GetRowId={(params) => String((params.data as ImportEditableRow).RowId)}
                EnableCellChangeFlash={false}
                RowHeight={65}
                gridApi={gridApi}
            />
        </div>
    )
}

export default ImportUsersGrid
